import DataType from '@opsbase/lib-datatype/DataType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import IssueStatus from '@opsbase/lib-issue/IssueStatus';
import Pages from '@opsbase/lib-pages';
import useConversationUpdate from '@opsbase/react-gql-hooks/conversation/useConversationUpdate';
import useIssueUpdate from '@opsbase/react-gql-hooks/issue/useIssueUpdate';
import DataPill from '@opsbase/web-chips/DataPill';
import IssueStatusChip from '@opsbase/web-chips/IssueStatusChip/IssueStatusChip';
import SPTChip from '@opsbase/web-chips/SPTChip';
import AssetChooser from '@opsbase/web-data-choosers/AssetChooser/AssetChooser';
import Divider from '@opsbase/web-layout/Divider/Divider';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import Link from '@opsbase/web-nav/Link';
import useNav from '@opsbase/web-nav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import DatePicker from '@opsbase/web-pickers/DatePicker';
import FlagPicker from '@opsbase/web-pickers/FlagPicker';
import IssueTypePicker from '@opsbase/web-pickers/IssueTypePicker';
import Button from '@opsbase/web-ui/Button';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useEffect, useState } from 'react';
export function IssueEditMainPane({ issue, onChange, }) {
    var _a, _b;
    const { id } = useParams();
    const { goTo } = useNav();
    const updateIssue = useIssueUpdate();
    const updateConversation = useConversationUpdate();
    const [hasAttemptedIssueUpdate, setHasAttemptedIssueUpdate] = useState(false);
    const disableEditing = (issue === null || issue === void 0 ? void 0 : issue.status) === IssueStatus.CLOSED || issue.status === IssueStatus.READ_ONLY;
    const issueOp = issue === null || issue === void 0 ? void 0 : issue.op;
    useEffect(() => {
        if (!hasAttemptedIssueUpdate && issue && !issue.conversation) {
            setHasAttemptedIssueUpdate(true);
            updateIssue({ id: issue.id, patch: { title: issue.title } }).catch(doNothing);
        }
        if (!hasAttemptedIssueUpdate && (issue === null || issue === void 0 ? void 0 : issue.conversation) && !issue.conversation.relatedIssue) {
            setHasAttemptedIssueUpdate(true);
            updateConversation({
                id: issue.conversation.id,
                patch: { relatedIssue: { id: issue.id } },
            }).catch(doNothing);
        }
    }, [hasAttemptedIssueUpdate, issue, updateConversation, updateIssue]);
    const handleClickOp = useCallback(() => {
        goTo(Pages.OP_VIEW, { id: issueOp.id });
    }, [goTo, issueOp]);
    const handleChangeFlag = useCallback(newFlag => onChange({ flag: newFlag }), [onChange]);
    const handleChangeType = useCallback(newType => onChange({ type: newType }), [onChange]);
    const handleChangeDeadline = useCallback(newDeadlineDate => onChange({ deadlineDate: newDeadlineDate }), [onChange]);
    const handleChangeAsset = useCallback(newAsset => {
        var _a;
        onChange({ asset: newAsset.length ? { id: (_a = newAsset[0]) === null || _a === void 0 ? void 0 : _a.id } : null });
    }, [onChange]);
    const handleClickReassign = useCallback(() => goTo(Pages.ISSUES_REASSIGN, { id }), [goTo, id]);
    const flag = issue && (React.createElement(FlagPicker, { noMargin: true, white: true, disabled: disableEditing, value: issue.flag, onChange: handleChangeFlag }));
    const op = (issue === null || issue === void 0 ? void 0 : issue.op) && (React.createElement(Link, { onClick: handleClickOp, noWrap: true }, issue.op.title));
    const type = issue && (React.createElement(IssueTypePicker, { noMargin: true, white: true, disabled: disableEditing, value: issue.type, onChange: handleChangeType }));
    const asset = issue && (React.createElement(AssetChooser, { selectLabel: i18n.t('select-asset'), disabled: disableEditing, value: issue.asset ? [issue.asset] : [], filters: { place: issue.place ? [(_a = issue.place) === null || _a === void 0 ? void 0 : _a.id] : [] }, onChange: handleChangeAsset }));
    const status = issue && React.createElement(IssueStatusChip, { issueStatus: issue.status });
    const occurenceDate = issue && (React.createElement(DatePicker, { noMargin: true, style: { width: 126 }, disabled: true, value: issue.occurrenceDate || undefined, onChange: handleChangeDeadline }));
    const deadlineDate = issue && (React.createElement(Horizontal, { centered: true, gutter: true },
        React.createElement(DatePicker, { noMargin: true, style: { width: disableEditing ? 126 : 80 }, white: !disableEditing, disabled: disableEditing, value: issue.deadlineDate || undefined, onChange: handleChangeDeadline }),
        React.createElement("div", { style: { fontSize: 12, maxWidth: 80 } }, issue === null || issue === void 0 ? void 0 : issue.dueStatus)));
    const closedDate = issue && (React.createElement(Horizontal, { centered: true, gutter: true },
        React.createElement(DatePicker, { noMargin: true, style: { width: 126 }, disabled: true, value: issue.status === IssueStatus.CLOSED
                ? issue.closedDate || issue.updatedDate
                : undefined, onChange: handleChangeDeadline }),
        React.createElement("div", { style: { fontSize: 12, maxWidth: 80 } }, issue === null || issue === void 0 ? void 0 : issue.resolutionTime)));
    const reportedBy = (issue === null || issue === void 0 ? void 0 : issue.createdBy) && (React.createElement(DataPill, { value: issue.createdBy.title, type: DataType.STAFF, avatarUrl: issue.createdBy.profileImageUrl }));
    const closedBy = (issue === null || issue === void 0 ? void 0 : issue.closedBy) && (React.createElement(DataPill, { value: issue.closedBy.title, type: DataType.STAFF, avatarUrl: issue.closedBy.profileImageUrl }));
    const where = issue && React.createElement(DataPill, { type: DataType.PLACE, value: (_b = issue === null || issue === void 0 ? void 0 : issue.place) === null || _b === void 0 ? void 0 : _b.title });
    const assignee = issue && (React.createElement(Horizontal, null,
        React.createElement(Pane, null,
            React.createElement(SPTChip, { item: issue, prefix: "assignedTo" })),
        !disableEditing && (React.createElement(Pane, null,
            React.createElement(Button, { small: true, background: true, onClick: handleClickReassign, label: i18n.t('reassign') })))));
    // TODO refactor when associated integrations use separate DB records
    // and we can save and access links to the IQM workflow.
    const associatedIntegration = (issue === null || issue === void 0 ? void 0 : issue.associatedIntegrationId) && (React.createElement(Text, null, `${i18n.t('iqm')} ${issue.associatedIntegrationId}`));
    function renderItem(label, value) {
        return (React.createElement(Horizontal, { padding: true, centered: true, gutter: true, justify: 'flex-start', style: { minHeight: 32, maxWidth: 450 } },
            React.createElement(Pane, { shrink: true, right: true, style: { minWidth: 100 } },
                React.createElement(Text, { variant: "h3" },
                    label,
                    ":")),
            React.createElement(Pane, { grow: true, style: { paddingLeft: 8 } }, value)));
    }
    return (React.createElement(Vertical, { gutter: true, shrink: true },
        renderItem(i18n.t('place'), React.createElement(Text, null, where)),
        renderItem(i18n.t('status'), status),
        renderItem(i18n.t('type'), type),
        renderItem(i18n.t('flag'), flag),
        React.createElement(Divider, null),
        renderItem(i18n.t('opened-date'), occurenceDate),
        renderItem(i18n.t('deadline-date'), deadlineDate),
        renderItem(i18n.t('closed-date'), closedDate),
        React.createElement(Divider, null),
        renderItem(i18n.t('reported-by'), reportedBy),
        renderItem(i18n.t('closed-by'), closedBy),
        renderItem(i18n.t('assignee'), assignee),
        React.createElement(Divider, null),
        op && renderItem(i18n.t('issue-op'), op),
        renderItem(i18n.t('issue-asset'), asset),
        renderItem(i18n.t('associated-integration'), associatedIntegration)));
}
export default IssueEditMainPane;
